import React, { createContext, useContext, useState, useEffect } from "react";
import authInstance from "../authInstance";
import { normalizeRole } from "../utils/roleUtils";

const AppContext = createContext();

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const [cartLength, setCartLength] = useState(0);
  const [selected, setSelected] = useState("");

  // Initialize from storage
  useEffect(() => {
    const storedUser = sessionStorage.getItem("user");
    const storedToken = sessionStorage.getItem("token");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    if (storedToken) {
      setToken(storedToken);
    }
  }, []);

  // Fetch cart count on refresh/init (only for regular users, not admins)
  useEffect(() => {
    const fetchCartCount = async () => {
      if (token && user) {
        // Only fetch cart for regular users/employees, not super admins or org admins
        const userRole = normalizeRole(user);
        if (userRole === 'user') {
          try {
            const response = await authInstance.get("/user/cart");
            if (response?.status === 200) {
              setCartLength(response?.data?.items?.length || 0);
            }
          } catch (error) {
            console.error("Error fetching cart count:", error);
          }
        } else {
          // Clear cart length for admin users
          setCartLength(0);
        }
      }
    };

    fetchCartCount();
  }, [token, user]);

  const login = (userData, userToken) => {
    setUser(userData);
    setToken(userToken);
    sessionStorage.setItem("user", JSON.stringify(userData));
    sessionStorage.setItem("token", userToken);

    // Set details object with orgId and user identifiers for all components
    const details = {
      orgId: userData.orgId || userData.org_id,
      _id: userData._id,
      uid: userData.uid,
      user_id: userData.user_id,
      customerId: userData.uid || userData.user_id,
      role: userData.role
    };
    sessionStorage.setItem("details", JSON.stringify(details));
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    sessionStorage.clear();
  };

  return (
    <AppContext.Provider
      value={{
        user,
        token,
        roleType: normalizeRole(user),
        login,
        logout,
        refresh,
        setRefresh,
        cartLength,
        setCartLength,
        selected,
        setSelected
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};
